import { CoreButtonView, CoreImageView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps, type TCoreTextStyleProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { TRowComponentProps, TViewCommonOption, TWidgetCommonOption } from '@libs/types';
import React, { MouseEventHandler } from 'react';
import { getSemanticColors, memoByPalette, SemanticColor, TSemanticColors } from '../../../theme/colors';
import { TRowOption } from '../../../types/options';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import { getVisualIconMap } from '../../Image/styles/visualUtils';
import { VisualView } from '../../Image/VisualView';
import styles from './styles/rowRegular.module.css';

export type TRowRegularViewProps = {
    /** Design version of the widget rendering the row (`TWidget.version`). */
    version: number | undefined;
    widget?: TCoreButtonViewProps['widget'];
    uid?: string;
    title?: string;
    subtitle?: string;
    metaText?: string;
    topText?: string;
    value?: boolean;
    iconName?: string;
    flagIcon?: string;
    avatar?: string;
    subtitleIconName?: string;
    iconHierarchy?: TRowOption['iconHierarchy'];
    iconVariant?: TRowOption['iconVariant'];
    metaVariant?: TRowOption['metaVariant'];
    metaHierarchy?: TRowOption['metaHierarchy'];
    variant?: 'CHECK';
    margin?: TWidgetCommonOption['margin'];
    isDisabled?: boolean;
    isHide?: boolean;
    handleClick: MouseEventHandler<HTMLButtonElement>;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
} & Pick<TRowComponentProps, 'isDisabled' | 'isHide' | 'isError'>;

/** Row palettes in the design of `colors`. */
const buildRowRegularStyleProps = (colors: TSemanticColors) => {
    const container: TCoreButtonStyleProps = {
        main: colors[SemanticColor.FillSecondary],
        onMain: colors[SemanticColor.TextPrimary],
        fillContainer: true,
        borderRadius: 6,
        gap: 0,
        padding: { top: 16, right: 24, bottom: 16, left: 24 },
        cursorDisabled: 'default',
        textAlign: 'start',
        disabled: {
            main: colors[SemanticColor.FillRowDisabled],
            onMain: colors[SemanticColor.TextPrimaryDisabled],
        },
        hovered: {
            main: colors[SemanticColor.FillRowHovered],
        },
        pressed: {
            main: colors[SemanticColor.FillRowPressed],
        },
    };

    const title: TCoreTextStyleProps = {
        ...TextStyle.main,
        main: colors[SemanticColor.TextPrimary],
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    };

    const subtitle: TCoreTextStyleProps = {
        ...TextStyle.secondary,
        main: colors[SemanticColor.TextSecondary],
        margin: { top: 4 },
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    };

    const metaSecondary: TCoreTextStyleProps = {
        ...TextStyle.secondary,
        main: colors[SemanticColor.TextSecondary],
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    };

    const metaPrimary: TCoreTextStyleProps = {
        ...TextStyle.main,
        main: colors[SemanticColor.TextPrimary],
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    };

    return {
        container,
        title,
        subtitle,
        metaSecondary,
        metaPrimary,
    };
};
const getRowRegularStyleProps = memoByPalette(buildRowRegularStyleProps);

export const RowRegularView = ({
    version,
    widget,
    uid = '',
    title,
    subtitle,
    metaText,
    topText,
    value = false,
    iconName,
    flagIcon,
    avatar,
    subtitleIconName,
    iconHierarchy,
    iconVariant,
    metaVariant,
    metaHierarchy,
    variant,
    margin,
    isDisabled,
    isError,
    isHide,
    handleClick,
    parentStyleNames = [],
}: TRowRegularViewProps) => {
    const hasLeadingIcon = !variant && (iconName || avatar || flagIcon);
    const effectiveMetaHierarchy = metaHierarchy ?? 'secondary';
    const semanticColors = getSemanticColors(version);
    const rowStyleProps = getRowRegularStyleProps(semanticColors);
    const metaStyleProps = effectiveMetaHierarchy === 'secondary' ? rowStyleProps.metaSecondary : rowStyleProps.metaPrimary;

    const renderTrailing = () => {
        if (variant === 'CHECK' && value) {
            return (
                <CoreImageView
                    src={getHref('ic_check', version)}
                    isDisabled={isDisabled}
                    parentStyleNames={[...parentStyleNames, 'rowRegularCheck']}
                    styleProps={{ width: 24, height: 24, ...getVisualIconMap(semanticColors).simple.emphasised }}
                />
            );
        }
        if (metaText) {
            void metaVariant;
            return (
                <CoreTextView
                    uid={'meta-' + uid}
                    text={metaText}
                    as="span"
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="rowRegularMeta"
                    styleProps={metaStyleProps}
                />
            );
        }
        return null;
    };

    return (
        <CoreButtonView
            widget={widget ?? ({ uid: uid, data: {}, options: {} } as TCoreButtonViewProps['widget'])}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={{
                ...rowStyleProps.container,
                margin: margin,
            }}
            parentStyleNames={parentStyleNames}
            styleName="rowRegular"
            onClick={handleClick}
        >
            {hasLeadingIcon && (
                <VisualView
                    version={version}
                    uid={`icon-${uid}`}
                    iconName={iconName}
                    avatar={avatar}
                    flagIcon={flagIcon}
                    iconHierarchy={iconHierarchy}
                    iconVariant={iconVariant}
                    size={'small'}
                    isDisabled={isDisabled}
                    rightMargin
                    parentStyleNames={parentStyleNames}
                    styleName="rowRegularIcon"
                />
            )}
            <div className={styles.rowContent}>
                <ConditionalRender condition={!!topText}>
                    <CoreTextView
                        uid={'topText-' + uid}
                        text={topText}
                        as="span"
                        isDisabled={isDisabled}
                        isError={isError}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularTopText"
                        styleProps={{
                            ...TextStyle.secondary,
                            main: semanticColors[SemanticColor.TextSecondary],
                            margin: { bottom: 4 },
                            disabled: {
                                main: semanticColors[SemanticColor.TextSecondary],
                            },
                            error: {
                                main: semanticColors[SemanticColor.TextError],
                            },
                        }}
                    />
                </ConditionalRender>
                <ConditionalRender condition={!!title}>
                    <CoreTextView
                        uid={'title-' + uid}
                        text={title}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularTitle"
                        styleProps={rowStyleProps.title}
                    />
                </ConditionalRender>
                <ConditionalRender condition={!!subtitle}>
                    <div className={styles.subtitleRow}>
                        <ConditionalRender condition={!!subtitleIconName}>
                            <CoreImageView
                                src={getHref(subtitleIconName, version)}
                                isDisabled={isDisabled}
                                parentStyleNames={parentStyleNames}
                                styleName={'rowRegularSubtitleIcon'}
                                styleProps={{
                                    width: 16,
                                    height: 16,
                                    iconColor: semanticColors[SemanticColor.IconSecondary],
                                    margin: { right: 8, top: 4 },
                                    disabled: {
                                        iconColor: semanticColors[SemanticColor.IconPrimaryDisabled],
                                    },
                                }}
                            />
                        </ConditionalRender>
                        <CoreTextView
                            uid={'subtitle-' + uid}
                            text={subtitle}
                            as="span"
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowRegularSubtitle"
                            styleProps={rowStyleProps.subtitle}
                        />
                    </div>
                </ConditionalRender>
            </div>
            {renderTrailing()}
        </CoreButtonView>
    );
};
